import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Palette, Eye, Layout, FileText, Star } from 'lucide-react';
import api from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

export default function StoreTemplates() {
  const [templates, setTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/tenant/templates');
      setTemplates(res.data.templates || []);
      setCurrentTemplate(res.data.current || '');
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal memuat templates', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (templateId) => {
    setSaving(true);
    try {
      await api.put(`/tenant/template?template_id=${templateId}`);
      setCurrentTemplate(templateId);
      toast({ title: 'Berhasil', description: 'Template berhasil diubah' });
    } catch (err) {
      toast({ title: 'Error', description: 'Gagal mengubah template', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="store-templates-page">
      <div>
        <h1 className="text-2xl font-bold">Template Toko</h1>
        <p className="text-muted-foreground">Pilih tampilan visual untuk storefront Anda</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card 
            key={template.id} 
            className={`cursor-pointer transition-all hover:shadow-lg ${currentTemplate === template.id ? 'ring-2 ring-primary' : ''}`}
            data-testid={`template-card-${template.id}`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{template.name}</CardTitle>
                <div className="flex items-center gap-1">
                  {template.is_custom && (
                    <Badge variant="outline" className="text-[10px]">Kustom</Badge>
                  )}
                  {currentTemplate === template.id && (
                    <Badge variant="default" className="bg-green-500">
                      <Check className="h-3 w-3 mr-1" /> Aktif
                    </Badge>
                  )}
                </div>
              </div>
              <CardDescription>{template.description}</CardDescription>
              {(template.modules_count > 0 || template.pages_count > 0) && (
                <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1">
                  <span className="flex items-center gap-1"><Star className="h-3 w-3" /> {template.modules_count} modul</span>
                  <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {template.pages_count} halaman</span>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Color Preview */}
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-muted-foreground" />
                <div className="flex gap-1">
                  <div 
                    className="w-6 h-6 rounded-full border" 
                    style={{ backgroundColor: template.colors?.primary }}
                    title="Primary"
                  />
                  <div 
                    className="w-6 h-6 rounded-full border" 
                    style={{ backgroundColor: template.colors?.secondary }}
                    title="Secondary"
                  />
                  <div 
                    className="w-6 h-6 rounded-full border" 
                    style={{ backgroundColor: template.colors?.accent }}
                    title="Accent"
                  />
                </div>
              </div>

              {/* Preview Area */}
              <div 
                className="h-32 rounded-lg border flex items-center justify-center"
                style={{ 
                  background: `linear-gradient(135deg, ${template.colors?.primary} 0%, ${template.colors?.secondary} 100%)` 
                }}
              >
                <span className="text-white font-semibold text-sm opacity-80">Preview</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button 
                  variant={currentTemplate === template.id ? "secondary" : "default"}
                  className="flex-1"
                  disabled={currentTemplate === template.id || saving}
                  onClick={() => handleSelectTemplate(template.id)}
                  data-testid={`select-template-${template.id}`}
                >
                  {currentTemplate === template.id ? 'Sedang Digunakan' : 'Pilih Template'}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  title="Preview tampilan template"
                  onClick={() => window.open(template.preview_url || `/template-preview/${template.id}`, '_blank', 'noopener,noreferrer')}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Custom Colors Section */}
      <Card>
        <CardHeader>
          <CardTitle>Kustomisasi Warna</CardTitle>
          <CardDescription>Sesuaikan warna template dengan brand Anda</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Fitur kustomisasi warna akan segera hadir. Untuk saat ini, gunakan template yang tersedia.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
